import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import { api } from '@/utils/api';
import {
  TrendingUp, TrendingDown, AlertTriangle, Sparkles, Target, Brain,
  ArrowUpRight, Activity, Building2, DollarSign, Wrench, Users,
  Loader2, CheckCircle2, ChevronRight, Send, MessageSquare, Zap,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Decision Center — REVA's strategic surface for executives.
//
//   "What is happening? What changed? Where is money being lost?
//    What requires attention? What should I do?"
//
// All numbers come from /api/decisions/* deterministic endpoints.
// AI is used ONLY to narrate / answer questions — never to invent scores.
// ─────────────────────────────────────────────────────────────────────────────

const tierColor = (tier) => ({
  excellent: { bg: 'bg-[rgba(0,168,107,0.08)]', border: 'border-[rgba(0,168,107,0.30)]', text: 'text-[#00A86B]', dot: 'bg-[#00A86B]' },
  good:      { bg: 'bg-[rgba(31,209,138,0.10)]', border: 'border-[rgba(31,209,138,0.35)]', text: 'text-[#0E8C5D]', dot: 'bg-[#1FD18A]' },
  watch:     { bg: 'bg-[rgba(212,175,55,0.10)]', border: 'border-[rgba(212,175,55,0.40)]', text: 'text-[#A07A0F]', dot: 'bg-[#D4AF37]' },
  critical:  { bg: 'bg-[rgba(220,38,38,0.06)]', border: 'border-[rgba(220,38,38,0.30)]', text: 'text-[#B91C1C]', dot: 'bg-[#DC2626]' },
  no_data:   { bg: 'bg-[#F1F3F6]', border: 'border-[#E1E6EC]', text: 'text-[#71777E]', dot: 'bg-[#9CA3AF]' },
}[tier] || { bg: 'bg-[#F1F3F6]', border: 'border-[#E1E6EC]', text: 'text-[#71777E]', dot: 'bg-[#9CA3AF]' });

const fmtSAR = (n) => `SAR ${Number(n || 0).toLocaleString()}`;

export default function DecisionCenter() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [propertyHealth, setPropertyHealth] = useState([]);
  const [roi, setRoi] = useState(null);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [asking, setAsking] = useState(false);
  const [activeRootCause, setActiveRootCause] = useState(null);
  const [rcLoading, setRcLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [p, ph, r, rc] = await Promise.all([
          api.getPortfolioHealth().catch(() => ({ data: null })),
          api.getPropertyHealthList().catch(() => ({ data: { properties: [] } })),
          api.getRoiOpportunities().catch(() => ({ data: null })),
          api.getDecisionRecommendations(8).catch(() => ({ data: { recommendations: [] } })),
        ]);
        if (!mounted) return;
        setPortfolio(p.data);
        setPropertyHealth(ph.data?.properties || []);
        setRoi(r.data);
        setRecs(rc.data?.recommendations || []);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const askDecisions = async (q) => {
    const text = (q ?? question).trim();
    if (!text || asking) return;
    setAsking(true); setAnswer(null);
    try {
      const r = await api.postDecisionsAsk(text, isAr ? 'ar' : 'en');
      setAnswer({ q: text, a: r.data.answer });
      setQuestion('');
    } catch (e) {
      setAnswer({ q: text, a: isAr ? 'تعذّر الاتصال بـ REVA.' : 'Could not reach REVA right now.' });
    } finally { setAsking(false); }
  };

  const runRootCause = async (metric) => {
    setActiveRootCause({ metric, loading: true });
    setRcLoading(true);
    try {
      const r = await api.postRootCause(metric);
      setActiveRootCause({ metric, data: r.data });
    } catch (e) {
      setActiveRootCause({ metric, error: true });
    } finally { setRcLoading(false); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[color:var(--reva-signal)]" />
        </div>
      </Layout>
    );
  }

  const portColor = tierColor(portfolio?.tier);
  const suggestedQuestions = [
    { en: 'Why did occupancy decline?',                    ar: 'لماذا تراجع الإشغال؟',                    metric: 'occupancy' },
    { en: 'Where am I losing the most money?',             ar: 'أين أخسر أكبر قدر من المال؟',           metric: null },
    { en: 'Which properties need attention now?',          ar: 'أي العقارات تحتاج اهتمامًا الآن؟',         metric: null },
    { en: 'How can I increase revenue this quarter?',      ar: 'كيف أرفع الإيرادات هذا الربع؟',           metric: null },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="decision-center">

        {/* HEADER */}
        <div className="reva-card px-6 py-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[rgba(0,168,107,0.08)] border border-[rgba(0,168,107,0.20)] flex items-center justify-center shrink-0">
              <Brain className="h-5 w-5 text-[color:var(--reva-signal)]" strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <div className="text-[10.5px] reva-mono tracking-[0.16em] uppercase text-[#71777E]">
                {isAr ? 'مركز قرارات REVA' : 'REVA Decision Center'}
              </div>
              <h1 className="font-heading text-xl sm:text-2xl text-[#0E1320] mt-1" data-testid="decision-center-title">
                {isAr ? 'افهم الأداء. اكتشف الفرصة. اتّخذ القرار.' : 'Understand performance. Uncover opportunity. Take action.'}
              </h1>
              <p className="text-[13px] text-[#4A5263] mt-1.5 leading-relaxed">
                {isAr
                  ? 'كل رقم هنا محسوب من بياناتك التشغيلية الحيّة. الذكاء الاصطناعي يشرح ولا يخترع.'
                  : 'Every number here is computed from your live operational data. AI narrates — never invents.'}
              </p>
            </div>
          </div>
        </div>

        {/* TOP STRIP — Portfolio Health + Total Opportunity */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Portfolio score */}
          <div className={`reva-card p-5 ${portColor.bg} ${portColor.border} md:col-span-1`} data-testid="portfolio-health-card">
            <div className="flex items-center justify-between">
              <div className="text-[10.5px] reva-mono tracking-[0.16em] uppercase text-[#71777E]">
                {isAr ? 'صحّة المحفظة' : 'Portfolio health'}
              </div>
              <span className={`text-[10px] reva-mono uppercase tracking-[0.16em] ${portColor.text}`}>{portfolio?.tier || '—'}</span>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className={`text-5xl font-bold ${portColor.text}`} data-testid="portfolio-score">{portfolio?.score ?? 0}</span>
              <span className="text-[14px] text-[#71777E]">/ 100</span>
            </div>
            <p className="text-[12px] text-[#4A5263] mt-2 leading-relaxed">{portfolio?.explanation_en}</p>
            <div className="mt-3 text-[11px] text-[#71777E]">
              {portfolio?.property_count || 0} {isAr ? 'عقار' : 'properties'} · {isAr ? 'متوسط مرجّح' : 'weighted avg'} {portfolio?.weighted_average}
            </div>
          </div>

          {/* Total ROI Opportunity — HERO of the page */}
          <div className="reva-card p-6 md:col-span-2 relative overflow-hidden" data-testid="roi-summary-card">
            <div className="absolute -top-16 -end-16 w-56 h-56 rounded-full pointer-events-none" style={{ background: 'radial-gradient(closest-side, rgba(0,168,107,0.22), transparent 70%)' }} />
            <div className="absolute -bottom-12 -start-12 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'radial-gradient(closest-side, rgba(212,175,55,0.12), transparent 70%)' }} />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 text-[10px] reva-mono tracking-[0.18em] uppercase font-semibold px-2.5 py-1 rounded-full bg-[color:var(--reva-signal)]/12 border border-[color:var(--reva-signal)]/30 text-[color:var(--reva-signal)]" data-testid="roi-ai-badge">
                <Sparkles className="h-3 w-3" />
                {isAr ? 'اكتشف REVA' : 'REVA uncovered'}
              </div>
              <div className="mt-3 text-[11px] reva-mono tracking-[0.16em] uppercase text-[#71777E]">
                {isAr ? 'فرصة سنويّة' : 'Annual opportunity'}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-[56px] sm:text-[64px] leading-none font-bold text-[#0E1320] tracking-tight" data-testid="roi-total">
                  {roi ? fmtSAR(roi.total_opportunity_annual) : '—'}
                </span>
              </div>
              <p className="text-[14px] sm:text-[15px] text-[#0E1320] mt-3 leading-relaxed max-w-xl font-medium" data-testid="roi-narrative">
                {isAr
                  ? `REVA اكتشف ${fmtSAR(roi?.total_opportunity_annual || 0)} من الإيرادات والاستردادات الممكنة داخل محفظتك.`
                  : `REVA found ${roi ? fmtSAR(roi.total_opportunity_annual) : ''} of recoverable + uplift opportunity sitting inside your portfolio.`}
              </p>
              <p className="text-[12px] text-[#71777E] mt-1.5 leading-relaxed max-w-xl">
                {isAr
                  ? 'موزّعة عبر إشغال الوحدات الشاغرة، تسرّب الإيجارات، ترقية التجديدات، وتحصيل المتأخّرات.'
                  : 'Distributed across vacancy backfill, rent leakage, renewal uplifts, and collections recovery.'}
              </p>
              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
                {(roi?.breakdown || []).map(b => (
                  <div key={b.id} className="rounded-lg bg-white border border-[#ECEFF3] p-3 hover:border-[color:var(--reva-signal)]/30 transition-colors" data-testid={`roi-${b.id}`}>
                    <div className="text-[10px] reva-mono uppercase text-[#71777E] tracking-wider">{isAr ? b.title_ar : b.title_en}</div>
                    <div className="mt-1 text-[16px] font-bold text-[#0E1320]">{fmtSAR(b.amount)}</div>
                    <div className="text-[10px] text-[#71777E] mt-0.5">{b.count} {isAr ? 'بند' : 'items'} · {Math.round(b.confidence * 100)}% {isAr ? 'ثقة' : 'conf.'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ASK REVA (NL Q&A) */}
        <div className="reva-card p-5" data-testid="decisions-ask">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="h-4 w-4 text-[color:var(--reva-signal)]" />
            <div className="text-[12px] font-semibold text-[#0E1320]">
              {isAr ? 'اسأل REVA · أجوبة مدعّمة بأرقام محفظتك' : 'Ask REVA · Grounded in your portfolio numbers'}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ask-suggestions">
            {suggestedQuestions.map((s, i) => (
              <button
                key={i}
                onClick={() => askDecisions(isAr ? s.ar : s.en)}
                disabled={asking}
                className="text-[11.5px] px-3 py-1.5 rounded-full border border-[#E1E6EC] bg-white hover:border-[color:var(--reva-signal)]/40 hover:bg-[color:var(--reva-signal)]/[0.06] text-[#1A1F2A] transition-colors disabled:opacity-50"
                data-testid={`ask-suggest-${i}`}
              >
                {isAr ? s.ar : s.en}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-full bg-[#F7F8FA] border border-[#E1E6EC] focus-within:border-[color:var(--reva-signal)] px-4 py-2 transition-colors">
            <input
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && askDecisions()}
              placeholder={isAr ? 'اطرح سؤالاً عن المحفظة، الإيرادات، المخاطر…' : 'Ask about portfolio, revenue, risks…'}
              disabled={asking}
              className="flex-1 bg-transparent text-[13px] text-[#0E1320] placeholder:text-[#9CA3AF] outline-none"
              data-testid="ask-input"
            />
            <button
              onClick={() => askDecisions()}
              disabled={asking || !question.trim()}
              className="w-7 h-7 rounded-full bg-[color:var(--reva-signal)] hover:bg-[#00734A] text-white flex items-center justify-center transition-colors disabled:opacity-40"
              data-testid="ask-send"
            >
              {asking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            </button>
          </div>
          {answer && (
            <div className="mt-4 rounded-xl bg-[#F7F8FA] border border-[#ECEFF3] p-4" data-testid="ask-answer">
              <div className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[color:var(--reva-signal)] mb-1.5">
                <Sparkles className="h-3 w-3 inline me-1" /> {isAr ? 'إجابة REVA' : 'REVA reply'}
              </div>
              <div className="text-[11px] text-[#71777E] mb-2 italic">&ldquo;{answer.q}&rdquo;</div>
              <div className="text-[13px] text-[#0E1320] leading-relaxed whitespace-pre-wrap">{answer.a}</div>
            </div>
          )}
        </div>

        {/* RECOMMENDED ACTIONS */}
        <div className="reva-card" data-testid="recommendations-section">
          <div className="px-5 py-3 border-b border-[#ECEFF3] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-[color:var(--reva-signal)]" />
              <div className="text-[12.5px] font-semibold text-[#0E1320]">
                {isAr ? 'إجراءات موصى بها · مرتّبة حسب الأثر' : 'Recommended actions · ranked by impact'}
              </div>
            </div>
            <span className="text-[10.5px] reva-mono uppercase text-[#71777E] tracking-wider">
              {recs.length} {isAr ? 'إجراء' : 'actions'}
            </span>
          </div>
          {recs.length === 0 ? (
            <div className="px-5 py-8 text-center text-[12.5px] text-[#71777E]">
              <CheckCircle2 className="h-6 w-6 text-[color:var(--reva-signal)] mx-auto mb-2" />
              {isAr ? 'لا توجد إجراءات عاجلة — المحفظة بحالة جيّدة.' : 'No urgent actions — portfolio is in good shape.'}
            </div>
          ) : (
            <div className="divide-y divide-[#ECEFF3]">
              {recs.map((r, i) => {
                // Derive priority from $ impact × confidence
                const impactValue = Number(r.expected_impact?.value || 0);
                const conf = Number(r.confidence || 0);
                const score = impactValue * conf;
                const priority = score >= 20000 ? 'P0' : score >= 5000 ? 'P1' : 'P2';
                const pStyle = priority === 'P0'
                  ? { bg: 'bg-[#FEE2E2]', text: 'text-[#B91C1C]', border: 'border-[#FCA5A5]' }
                  : priority === 'P1'
                    ? { bg: 'bg-[rgba(212,175,55,0.15)]', text: 'text-[#A07A0F]', border: 'border-[rgba(212,175,55,0.40)]' }
                    : { bg: 'bg-[#F1F3F6]', text: 'text-[#4A5263]', border: 'border-[#E1E6EC]' };
                // Suggested owner by category
                const ownerMap = {
                  collections:           { en: 'Finance team',     ar: 'فريق المالية' },
                  revenue_optimization:  { en: 'Property Manager', ar: 'مدير العقار' },
                  lease_renewal:         { en: 'Leasing team',     ar: 'فريق التأجير' },
                  maintenance:           { en: 'Operations team',  ar: 'فريق العمليّات' },
                };
                const owner = ownerMap[r.category] || { en: 'Operations', ar: 'العمليّات' };
                // Time-to-impact
                const ttiMap = {
                  collections: { en: '30 days',           ar: '٣٠ يومًا' },
                  revenue_optimization:  { en: 'Next renewal',     ar: 'التجديد القادم' },
                  lease_renewal:         { en: 'Pre-expiry window', ar: 'قبل انتهاء العقد' },
                  maintenance:           { en: '< 30 days',         ar: '< ٣٠ يومًا' },
                };
                const tti = ttiMap[r.category] || { en: 'Soon', ar: 'قريبًا' };

                return (
                  <div key={r.id} className="px-5 py-4 hover:bg-[#FAFBFC] transition-colors" data-testid={`rec-${r.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-[color:var(--reva-signal)]/10 border border-[color:var(--reva-signal)]/25 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[11px] font-semibold text-[color:var(--reva-signal)]">{i + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Priority badge + category */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-[10px] reva-mono font-bold tracking-[0.16em] px-2 py-0.5 rounded-full border ${pStyle.bg} ${pStyle.border} ${pStyle.text}`} data-testid={`rec-priority-${r.id}`}>
                            {priority}
                          </span>
                          <span className="text-[10px] reva-mono uppercase tracking-wider text-[#71777E]">
                            {r.category.replace(/_/g, ' ')}
                          </span>
                        </div>
                        {/* Title */}
                        <div className="text-[13.5px] font-semibold text-[#0E1320] leading-snug" data-testid={`rec-title-${r.id}`}>
                          {isAr ? r.title_ar : r.title_en}
                        </div>
                        {/* Consulting-grade triplet: Problem / Opportunity / Action */}
                        <div className="mt-2.5 space-y-1.5">
                          <div className="flex gap-2 text-[11.5px]">
                            <span className="reva-mono uppercase tracking-wider text-[#71777E] shrink-0 w-[100px]">{isAr ? 'الفرصة' : 'Opportunity'}</span>
                            <span className="text-[#0E1320] flex-1 leading-relaxed">{isAr ? r.rationale_ar : r.rationale_en}</span>
                          </div>
                          <div className="flex gap-2 text-[11.5px]">
                            <span className="reva-mono uppercase tracking-wider text-[#71777E] shrink-0 w-[100px]">{isAr ? 'الأثر المتوقّع' : 'Estimated impact'}</span>
                            <span className="font-semibold text-[color:var(--reva-signal)] flex-1" data-testid={`rec-impact-${r.id}`}>
                              {isAr ? r.expected_impact?.label_ar : r.expected_impact?.label_en}
                            </span>
                          </div>
                        </div>
                        {/* Footer meta */}
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-[10.5px] text-[#71777E]">
                          <span className="inline-flex items-center gap-1"><Activity className="h-3 w-3" /> {isAr ? tti.ar : tti.en}</span>
                          <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {isAr ? owner.ar : owner.en}</span>
                          <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" /> {Math.round(conf * 100)}% {isAr ? 'ثقة' : 'confidence'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* PROPERTY HEALTH GRID */}
        <div className="reva-card" data-testid="property-health-grid">
          <div className="px-5 py-3 border-b border-[#ECEFF3] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[color:var(--reva-signal)]" />
              <div className="text-[12.5px] font-semibold text-[#0E1320]">
                {isAr ? 'صحّة كل عقار' : 'Per-property health'}
              </div>
            </div>
            <span className="text-[10.5px] reva-mono uppercase text-[#71777E] tracking-wider">
              {propertyHealth.length} {isAr ? 'عقار' : 'properties'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {propertyHealth.map(ph => {
              const c = tierColor(ph.tier);
              return (
                <div key={ph.property_id} className={`rounded-xl border ${c.border} ${c.bg} p-3.5`} data-testid={`prop-health-${ph.property_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-[#0E1320] truncate" title={ph.property_name}>
                        {ph.property_name}
                      </div>
                      <div className="text-[10.5px] text-[#71777E] mt-0.5">{ph.city || '—'}</div>
                    </div>
                    <div className={`text-2xl font-bold ${c.text}`}>{ph.score}</div>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-1">
                    {(ph.components || []).slice(0, 6).map(comp => {
                      const cc = tierColor(comp.tier);
                      return (
                        <div key={comp.component} className="rounded bg-white/60 px-1.5 py-1 border border-white/80" title={comp.label_en}>
                          <div className="text-[8.5px] reva-mono uppercase text-[#71777E] truncate">{comp.component.replace(/_/g, ' ').slice(0, 8)}</div>
                          <div className={`text-[11px] font-semibold ${cc.text}`}>{comp.score}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-[10.5px] text-[#4A5263] leading-snug line-clamp-2">{ph.explanation_en}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ROOT-CAUSE DRILL */}
        <div className="reva-card p-5" data-testid="root-cause-section">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-4 w-4 text-[color:var(--reva-signal)]" />
            <div className="text-[12.5px] font-semibold text-[#0E1320]">
              {isAr ? 'تحليل السبب الجذري — ماذا تغيّر ولماذا' : 'Root-cause — what changed and why'}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {[
              { id: 'occupancy',           en: 'Occupancy',           ar: 'الإشغال',         icon: Users },
              { id: 'revenue',             en: 'Revenue',             ar: 'الإيرادات',        icon: DollarSign },
              { id: 'collections',         en: 'Collections',         ar: 'التحصيلات',        icon: TrendingUp },
              { id: 'maintenance_backlog', en: 'Maintenance backlog', ar: 'تراكم الصيانة',    icon: Wrench },
              { id: 'tenant_risk',         en: 'Tenant risk',         ar: 'مخاطر المستأجرين', icon: AlertTriangle },
            ].map(m => {
              const Icon = m.icon;
              const active = activeRootCause?.metric === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => runRootCause(m.id)}
                  disabled={rcLoading}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11.5px] transition-colors ${
                    active ? 'bg-[color:var(--reva-signal)]/10 border-[color:var(--reva-signal)]/40 text-[color:var(--reva-signal)]' : 'bg-[#F7F8FA] border-[#E1E6EC] text-[#1A1F2A] hover:border-[color:var(--reva-signal)]/30'
                  } disabled:opacity-50`}
                  data-testid={`rc-${m.id}`}
                >
                  <Icon className="h-3 w-3" />
                  {isAr ? m.ar : m.en}
                </button>
              );
            })}
          </div>
          {activeRootCause && (
            <div className="mt-4 rounded-xl bg-[#F7F8FA] border border-[#ECEFF3] p-4" data-testid="rc-result">
              {activeRootCause.loading || rcLoading ? (
                <div className="flex items-center gap-2 text-[12px] text-[#71777E]">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> {isAr ? 'يحلّل…' : 'Analyzing…'}
                </div>
              ) : activeRootCause.error ? (
                <div className="text-[12px] text-[#B91C1C]">{isAr ? 'تعذّر التحليل' : 'Analysis failed'}</div>
              ) : (
                <div>
                  <div className="text-[13px] text-[#0E1320] leading-relaxed">{activeRootCause.data?.explanation_en}</div>
                  <div className="mt-3 space-y-1.5">
                    {(activeRootCause.data?.worst_drivers || []).map((d, i) => (
                      <div key={d.property_id} className="flex items-center gap-2 text-[12px] text-[#4A5263]" data-testid={`rc-driver-${i}`}>
                        <span className="w-5 h-5 rounded-full bg-[#FEE2E2] text-[#B91C1C] text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
                        <span className="font-medium text-[#0E1320]">{d.property_name}</span>
                        <span className="text-[#71777E]">· {d.label_en}</span>
                        <span className="ms-auto reva-mono">{d.component_score}/100</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* CTA — convert wins */}
        <div className="reva-card-emerald p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="decisions-cta">
          <Zap className="h-5 w-5 text-[color:var(--reva-signal)] shrink-0" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#0E1320]">
              {isAr ? 'كل قرار هنا قابل للتنفيذ مباشرة' : 'Every decision here is one click away from execution'}
            </div>
            <div className="text-[11.5px] text-[#4A5263] mt-1">
              {isAr
                ? 'حوّل أيّ توصية إلى سير عمل بموافقات متعدّدة المراحل.'
                : 'Convert any recommendation into a multi-stage approval workflow.'}
            </div>
          </div>
          <button onClick={() => navigate('/tasks')} className="reva-btn-emerald-l text-[12px] py-2 px-4" data-testid="decisions-go-tasks">
            {isAr ? 'افتح مركز المهام' : 'Open task center'} <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>

      </div>
    </Layout>
  );
}
